import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_URL } from "../../utils/config";
import {
  codeToDigits,
  normalizeVerificationCode,
} from "../../utils/loginVerifyUtils";
import { clearDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";
import { subscribeConsent } from "../../utils/consent";
import { trackRegistration } from "../../utils/klaviyo";
import "./Register.css";

const baseUrl = API_URL;

const VerifyCode = ({ email, initialCode = "" }) => {
  const navigate = useNavigate();
  const CODE_LENGTH = 6;

  const [codeDigits, setCodeDigits] = useState(() => codeToDigits(initialCode));
  const [verifyError, setVerifyError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timerRunning, setTimerRunning] = useState(true);
  const [timeLeft, setTimeLeft] = useState(120000);
  const [isVerified, setIsVerified] = useState(false);

  const inputRefs = useRef([]);
  const hasAutoSubmittedRef = useRef(false);

  useEffect(() => {
    if (!isVerified) return;
    const send = () => trackRegistration("completed", "Venue", email);
    send();
    return subscribeConsent(send);
  }, [isVerified, email]);

  const getLangFromLocalStorage = () =>
    localStorage.getItem("language") || "en";

  useEffect(() => {
    let timer;
    if (timerRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1000) {
            clearInterval(timer);
            setTimerRunning(false);
            return 0;
          }
          return prevTime - 1000;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerRunning, timeLeft]);

  useEffect(() => {
    if (verifyError) {
      const timer = setTimeout(() => setVerifyError(""), 15000);
      return () => clearTimeout(timer);
    }
  }, [verifyError]);

  useEffect(() => {
    if (resendError) {
      const timer = setTimeout(() => setResendError(""), 15000);
      return () => clearTimeout(timer);
    }
  }, [resendError]);

  const handleDigitChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newCodeDigits = [...codeDigits];
    newCodeDigits[index] = value;
    setCodeDigits(newCodeDigits);
    setVerifyError("");
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const verifyCodeAPI = async (code) => {
    const response = await fetch(`${baseUrl}/api/v1/attnd-venue/auth/verify-code`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
      body: JSON.stringify({ email, code, type: "register" }),
    });
    if (!response.ok) {
      const errorResponse = await response.json();
      throw errorResponse;
    }
    return response.json();
  };

  const submitVerification = useCallback(
    async (rawCode) => {
      const code = normalizeVerificationCode(rawCode);
      if (code.length !== CODE_LENGTH) {
        setVerifyError("Please enter the complete code.");
        return;
      }
      if (isVerifying) {
        return;
      }

      setIsVerifying(true);
      setVerifyError("");

      try {
        const response = await verifyCodeAPI(code);
        if (response.message) {
          clearDeepLinkVerificationCode("register", email);
          trackRegistration("completed", "Venue", email);
          setIsVerified(true);
          setTimeout(() => navigate("/login"), 2000);
        } else {
          setVerifyError("Invalid verification code. Please try again.");
        }
      } catch (error) {
        const errorMessage =
          error.message ||
          error.error ||
          "An error occurred during verification. Please try again.";
        setVerifyError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    },
    [email, isVerifying, navigate],
  );

  const handleVerifyCodeSubmit = async (e) => {
    e.preventDefault();
    await submitVerification(codeDigits.join(""));
  };

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (normalized.length !== CODE_LENGTH) {
      return;
    }
    setCodeDigits(normalized.split(""));
  }, [initialCode]);

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (
      hasAutoSubmittedRef.current ||
      normalized.length !== CODE_LENGTH ||
      !email
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    submitVerification(normalized);
  }, [initialCode, email, submitVerification]);

  const resendCodeAPI = async () => {
    const response = await fetch(`${baseUrl}/api/v1/attnd-venue/auth/resend-code`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const errorResponse = await response.json();
      throw errorResponse;
    }
    return response.json();
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendError("");
    setResendMessage("");
    try {
      const response = await resendCodeAPI();
      if (response.message) {
        setResendMessage(response.message);
        setTimeLeft(120000);
        setTimerRunning(true);
      } else {
        setResendError("Failed to resend verification code.");
      }
    } catch (error) {
      const errorMessage =
        error.error || "An error occurred while resending the code.";
      setResendError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  if (isVerified) {
    return (
      <div className="success-message">
        <img src="/tick-circle.svg" alt="Success" className="success-icon" />
        <h1>Your Account has been Verified</h1>
        <p style={{ marginBottom: "20px", textAlign: "center" }}>
          You will be redirected to the login screen to access your account.
        </p>
        <button
          className="login-btn"
          type="button"
          onClick={() => navigate("/login")}
        >
          Continue to Login Page
        </button>
      </div>
    );
  }

  return (
    <div className="verify-code-container">
      <h2 className="title-login">Verify Code</h2>
      <p className="subtitle-login">Enter the code received on your email</p>
      <form onSubmit={handleVerifyCodeSubmit}>
        <div className="form-group-login">
          <div className="digits-container">
            {codeDigits.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleDigitChange(e.target.value, index)}
                onFocus={(e) => e.target.select()}
                ref={(el) => (inputRefs.current[index] = el)}
                className="digit-input"
                inputMode="numeric"
                disabled={isVerifying}
              />
            ))}
          </div>
        </div>
        <div className="form-group-login">
          <button
            className="login-btn"
            type="submit"
            disabled={codeDigits.some((dig) => dig === "") || isVerifying}
          >
            {isVerifying ? "Verifying..." : "Submit"}
          </button>
        </div>
        {resendMessage && <p className="resend-btn">{resendMessage}</p>}
        {resendError && <p className="error">{resendError}</p>}
        {verifyError && <p className="error">{verifyError}</p>}
      </form>
      <div className="form-group-login">
        {timerRunning ? (
          <p className="resend-btn">
            Resend Code in {formatTime(timeLeft / 1000)}
          </p>
        ) : (
          <button
            className="resend-btn"
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
          >
            {isResending ? "Resending..." : "Resend Code"}
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyCode;
